<template>
    <main-layout>
        <div class="page-head" slot="header">
            <div><div class="page-kicker">Accounting</div><h2>Owner statements</h2></div>
        </div>

        <div class="page-container">
            <!-- 输入项 -->
            <section class="page-section form-section">
                <el-card class="form-card">
                    <div slot="header" class="form-header">
                        <span><i class="el-icon-setting"></i> Merge Configuration</span>
                    </div>

                    <!-- 基本信息输入 -->
                    <el-form :inline="true" size="medium" class="merge-form">
                        <!-- 日期选择 -->
                        <el-form-item label="Date Range" class="date-range-item">
                            <el-date-picker
                                v-model="dateRange"
                                type="daterange"
                                format="yyyy-MM-dd"
                                value-format="yyyy-MM-dd"
                                range-separator="-"
                                start-placeholder="Start Date"
                                end-placeholder="End Date"
                                class="date-picker">
                            </el-date-picker>
                        </el-form-item>

                        <!-- 选择 Statement Type -->
                        <el-form-item label="Statement Type">
                            <el-select v-model="mergeType" placeholder="Select Type" class="type-select">
                                <el-option label="Strata" value="strata"></el-option>
                                <el-option label="Rental" value="rental"></el-option>
                            </el-select>
                        </el-form-item>

                        <!-- 归档路径选择 -->
                        <el-form-item label="Archive Path" class="archive-path-item">
                            <el-cascader
                                v-model="selectedPath"
                                :options="pathOptions"
                                :props="{
                  value: 'id',
                  label: 'name',
                  children: 'children',
                  checkStrictly: true,
                  lazy: true,
                  lazyLoad: loadPathOptions
                }"
                                placeholder="Select a path"
                                clearable
                                @change="handlePathChange"
                                class="path-cascader">
                            </el-cascader>
                        </el-form-item>
                        <el-form-item label="Tag" class="tag-item">
                            <el-input v-model="tag" placeholder="Enter tag (optional)" clearable></el-input>
                        </el-form-item>
                    </el-form>

                    <!-- 按钮组 -->
                    <div class="action-buttons">
                        <el-upload
                            class="upload-demo"
                            :limit="1"
                            :show-file-list="true"
                            :http-request="handleUpload"
                            accept=".zip">
                            <el-button type="primary" icon="el-icon-upload">
                                Upload Statements (ZIP)
                            </el-button>
                        </el-upload>

                        <el-button
                            type="success"
                            @click="mergeStatements"
                            :loading="isLoading"
                            icon="el-icon-document-copy"
                            class="merge-button">
                            Merge Files
                        </el-button>
                    </div>
                </el-card>
            </section>

            <!-- 结果展示 -->
            <section class="page-section result-section">
                <el-card class="result-card">
                    <div slot="header" class="result-header">
                        <span><i class="el-icon-data-analysis"></i> Merge Results</span>
                    </div>

                    <!-- 无结果时显示 -->
                    <div v-if="!fileCount && !mergeResult" class="no-results">
                        <i class="el-icon-info empty-icon"></i>
                        <p>No merge operations performed yet. Upload statements and merge files to see results.</p>
                    </div>

                    <!-- 显示解压文件数量 -->
                    <div v-if="fileCount > 0" class="result-item">
                        <div class="result-title">
                            <i class="el-icon-folder-opened"></i>
                            <span>Extracted Files</span>
                        </div>
                        <el-alert
                            title="Files Extracted Successfully"
                            type="success"
                            :description="`${fileCount} files have been extracted and are ready to be merged.`"
                            show-icon>
                        </el-alert>
                    </div>

                    <!-- 显示合并结果 -->
                    <div v-if="mergeResult" class="result-item">
                        <div class="result-title">
                            <i class="el-icon-document-checked"></i>
                            <span>Merge Summary</span>
                        </div>

                        <div class="merge-stats">
                            <div class="stat-item success">
                                <div class="stat-number">{{ mergeResult.merged.count }}</div>
                                <div class="stat-label">Successfully Merged</div>
                            </div>
                            <div class="stat-item warning">
                                <div class="stat-number">{{ mergeResult.unmerged.saved_without_merge.count }}</div>
                                <div class="stat-label">Saved Without Merge</div>
                            </div>
                        </div>

                        <!-- 成功合并的文件列表 -->
                        <div v-if="mergeResult.merged.count > 0" class="merged-files">
                            <div class="result-title">
                                <i class="el-icon-success"></i>
                                <span>Successfully Merged Files</span>
                            </div>

                            <el-table
                                :data="mergeResult.merged.files"
                                style="width: 100%"
                                border
                                stripe>
                                <el-table-column
                                    prop="file_name"
                                    label="File Name"
                                    min-width="200">
                                </el-table-column>
                                <el-table-column
                                    prop="bills_count"
                                    label="Bills Merged"
                                    min-width="100">
                                </el-table-column>
                            </el-table>
                            <div class="total-invoice-count"
                                 style="margin-top: 10px; text-align: right; font-weight: bold;">
                                Total Invoices: {{ mergedInvoiceTotal }}
                            </div>
                        </div>

                        <!-- 未合并但已保存的文件列表 -->
                        <div v-if="mergeResult.unmerged.saved_without_merge.count > 0" class="saved-files">
                            <div class="result-title">
                                <i class="el-icon-info"></i>
                                <span>Saved Without Merge</span>
                            </div>

                            <el-table
                                :data="mergeResult.unmerged.saved_without_merge.files"
                                style="width: 100%"
                                border
                                stripe>
                                <el-table-column
                                    prop="file_name"
                                    label="File Name"
                                    min-width="200">
                                </el-table-column>
                                <el-table-column
                                    prop="reason"
                                    label="Reason"
                                    min-width="300">
                                </el-table-column>
                            </el-table>
                        </div>
                    </div>
                </el-card>
            </section>
        </div>

        <!-- 加载中提示 -->
        <el-dialog
            title="Processing"
            :visible.sync="showLoadingDialog"
            :close-on-click-modal="false"
            :close-on-press-escape="false"
            :show-close="false"
            width="400px"
            center
            class="loading-dialog">
            <div class="loading-content">
                <div class="loading-spinner">
                    <i class="el-icon-loading"></i>
                </div>
                <p class="loading-message">{{ loadingMessage }}</p>
                <el-progress
                    :percentage="mergeStatus.progress"
                    :status="mergeStatus.failed > 0 ? 'warning' : 'success'"
                    class="loading-progress">
                </el-progress>
                <div class="status-details" v-if="mergeStatus.total > 0">
                    <p>Total: {{ mergeStatus.total }}</p>
                    <p>Completed: {{ mergeStatus.completed }}</p>
                    <p>Failed: {{ mergeStatus.failed }}</p>
                    <p>Processing: {{ mergeStatus.processing }}</p>
                    <p>Pending: {{ mergeStatus.pending }}</p>
                </div>
            </div>
        </el-dialog>
    </main-layout>
</template>

<script>
import ApiService from "../utils/ApiService";

export default {
    name: "StatementMerge",
    data() {
        return {
            dateRange: [],
            mergeType: "",
            selectedPath: [],
            pathOptions: [],
            mergeResult: null,
            fileCount: 0,
            isLoading: false,
            showLoadingDialog: false,
            loadingMessage: "Processing, please wait...",
            jobIds: [],
            statusCheckInterval: null,
            mergeStatus: {
                total: 0,
                completed: 0,
                failed: 0,
                processing: 0,
                pending: 0,
                progress: 0
            },
            mergeDetails: [],
            tag: "",
            mergedInvoiceTotal: 0
        };
    },
    watch: {
        mergeType(newVal) {
            if (newVal) {
                this.selectedPath = [];
                this.loadPathOptions();
            } else {
                this.pathOptions = [];
            }
        },
        mergeResult: {
            handler(val) {
                if (val && val.merged && val.merged.files) {
                    this.mergedInvoiceTotal = val.merged.files.reduce((sum, f) => sum + (f.bills_count || 0), 0);
                } else {
                    this.mergedInvoiceTotal = 0;
                }
            },
            immediate: true,
            deep: true
        }
    },
    mounted() {
        // 不需要在 mounted 时加载，等待用户选择类型后再加载
    },
    beforeDestroy() {
        // 组件销毁前清除定时器
        if (this.statusCheckInterval) {
            clearInterval(this.statusCheckInterval);
        }
    },
    methods: {
        async loadPathOptions(node, resolve) {
            try {
                // 确保在调用 API 前已经选择了类型
                if (!this.mergeType) {
                    if (node) {
                        resolve([]);
                    } else {
                        this.pathOptions = [];
                    }
                    return;
                }

                const response = await ApiService.get("/buildium/statements/archives", {
                    params: {
                        type: this.mergeType,
                        parent_id: node ? node.value : 0
                    }
                });

                const paths = response.data.map(item => ({
                    ...item,
                    leaf: !item.has_children
                }));

                if (node) {
                    resolve(paths);
                } else {
                    this.pathOptions = paths;
                }
            } catch (error) {
                this.$message.error("Failed to load archive paths: " + error.message);
                if (node) {
                    resolve([]);
                }
            }
        },

        handlePathChange(value) {
            if (value && value.length > 0) {
                this.archivePath = value[value.length - 1];
            } else {
                this.archivePath = "";
            }
        },

        handleUpload(params) {
            let file = params.file;
            const formData = new FormData();
            formData.append("file", file);
            formData.append("type", this.mergeType);

            // 显示加载对话框
            this.loadingMessage = "Uploading and extracting files...";
            this.showLoadingDialog = true;

            ApiService.post("/buildium/statements/upload", formData, {
                headers: {"Content-Type": "multipart/form-data"}
            }).then(response => {
                this.$message.success(`Statements uploaded successfully! Extracted ${response.data.file_count} files.`);
                this.fileCount = response.data.file_count;
            }).catch(error => {
                this.$message.error("Upload failed: " + error.message);
            }).finally(() => {
                // 隐藏加载对话框
                this.showLoadingDialog = false;
            });
        },

        async mergeStatements() {
            if (!this.dateRange.length || !this.mergeType || !this.archivePath) {
                this.$message.warning("Please complete all fields.");
                return;
            }

            // 显示加载状态和对话框
            this.isLoading = true;
            this.loadingMessage = "Starting merge process...";
            this.showLoadingDialog = true;

            try {
                const response = await ApiService.post("/buildium/statements/merge", {
                    start_date: this.dateRange[0],
                    end_date: this.dateRange[1],
                    type: this.mergeType,
                    archive_path: this.archivePath,
                    tag: this.tag
                });

                this.jobIds = response.job_ids;
                this.mergeStatus.total = response.total_files;
                this.mergeStatus.progress = 0;

                // 开始定期检查状态
                this.startStatusCheck();

                this.$message.success("Merge process started successfully!");
            } catch (error) {
                this.$message.error("Failed to start merge process: " + error.message);
                this.isLoading = false;
                this.showLoadingDialog = false;
            }
        },

        startStatusCheck() {
            // 清除可能存在的旧定时器
            if (this.statusCheckInterval) {
                clearInterval(this.statusCheckInterval);
            }

            // 设置新的定时器，每2秒检查一次状态
            this.statusCheckInterval = setInterval(this.checkMergeStatus, 2000);
        },

        async checkMergeStatus() {
            try {
                const response = await ApiService.post("/buildium/statements/status", {
                    job_ids: this.jobIds
                });

                const {summary, details} = response;

                // 更新状态
                this.mergeStatus = summary;
                this.mergeDetails = details;

                // 更新加载对话框消息
                this.loadingMessage = `Processing: ${summary.progress}% complete (${summary.completed} completed, ${summary.failed} failed)`;

                // 如果所有任务都完成了，停止检查
                if (summary.progress === 100) {
                    clearInterval(this.statusCheckInterval);
                    this.isLoading = false;
                    this.showLoadingDialog = false;

                    // 显示最终结果
                    this.mergeResult = {
                        merged: {
                            count: summary.completed,
                            files: details.filter(d => d.status === "completed")
                        },
                        unmerged: {
                            saved_without_merge: {
                                count: summary.failed,
                                files: details.filter(d => d.status === "failed")
                            }
                        }
                    };

                    // 显示完成消息
                    if (summary.failed > 0) {
                        this.$message.warning(`Merge completed with ${summary.failed} failures.`);
                    } else {
                        this.$message.success("All files merged successfully!");
                    }
                }
            } catch (error) {
                console.error("Failed to check merge status:", error);
                // 不要在这里停止检查，因为可能是临时网络问题
            }
        }
    }
};
</script>

<style scoped>
.page-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

.page-header {
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 15px;
    margin-bottom: 20px;
}

.page-section {
    margin-bottom: 30px;
}

/* 表单样式 */
.form-card {
    margin-bottom: 30px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.form-header {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: bold;
}

.form-header i {
    margin-right: 8px;
    color: #409EFF;
}

.merge-form {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 20px;
}

.date-range-item {
    width: 100%;
    margin-bottom: 20px;
}

.date-picker {
    width: 100%;
}

.type-select, .path-select {
    width: 100%;
    min-width: 200px;
}

.archive-path-item {
    flex-grow: 1;
}

.action-buttons {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #ebeef5;
}

.merge-button {
    margin-left: 20px;
}

/* 结果展示样式 */
.result-card {
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.result-header {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: bold;
}

.result-header i {
    margin-right: 8px;
    color: #409EFF;
}

.no-results {
    text-align: center;
    padding: 40px 0;
    color: #909399;
}

.empty-icon {
    font-size: 48px;
    margin-bottom: 15px;
}

.result-item {
    margin-bottom: 25px;
}

.result-title {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 15px;
    color: #303133;
}

.result-title i {
    margin-right: 8px;
    color: #409EFF;
}

.merge-stats {
    display: flex;
    margin-bottom: 20px;
}

.stat-item {
    flex: 1;
    text-align: center;
    padding: 20px;
    border-radius: 4px;
    margin-right: 15px;
}

.stat-item:last-child {
    margin-right: 0;
}

.stat-item.success {
    background-color: #f0f9eb;
    border: 1px solid #e1f3d8;
}

.stat-item.warning {
    background-color: #fdf6ec;
    border: 1px solid #faecd8;
}

.stat-number {
    font-size: 32px;
    font-weight: bold;
    margin-bottom: 10px;
}

.success .stat-number {
    color: #67c23a;
}

.warning .stat-number {
    color: #e6a23c;
}

.stat-label {
    color: #606266;
}

.merged-files, .saved-files {
    margin-top: 20px;
    margin-bottom: 20px;
}

/* 加载对话框样式 */
.loading-content {
    text-align: center;
    padding: 20px 0;
}

.loading-spinner {
    font-size: 50px;
    color: #409EFF;
    margin-bottom: 20px;
}

.loading-message {
    font-size: 16px;
    color: #606266;
    margin-bottom: 20px;
}

.loading-progress {
    margin-top: 20px;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .merge-form {
        flex-direction: column;
    }

    .action-buttons {
        flex-direction: column;
        align-items: stretch;
    }

    .merge-button {
        margin-left: 0;
        margin-top: 15px;
    }

    .merge-stats {
        flex-direction: column;
    }

    .stat-item {
        margin-right: 0;
        margin-bottom: 15px;
    }
}

.path-cascader {
    width: 100%;
}

.status-details {
    margin-top: 20px;
    text-align: left;
    padding: 10px;
    background-color: #f5f7fa;
    border-radius: 4px;
}

.status-details p {
    margin: 5px 0;
    color: #606266;
    font-size: 14px;
}
</style>

<style>
/* 全局样式 */
.loading-dialog .el-dialog__header {
    padding: 15px;
    background-color: #409EFF;
    color: white;
}

.loading-dialog .el-dialog__title {
    color: white;
    font-weight: bold;
}

.loading-dialog .el-dialog__body {
    padding: 30px;
}

.el-progress-bar__inner {
    transition: all 0.8s ease;
}
</style>
